// packages
import classnames from 'classnames/bind';
import dayjs from 'dayjs';
import React, { useEffect, useState, useMemo } from 'react';
import { CSSTransition } from 'react-transition-group';
import { useParams } from 'react-router';
// custom
import Button from 'components/button';
import Loading from 'components/loading';
import Wrapper from 'components/wrapper';
import NotificationModal from 'components/notification-modal/notification-modal';
import ContactModal from 'components/contact-modal/contact-modal';
import Mentos from 'components/mentos';
import { COMPLETE_SESSION, FINISH_CHARGING, FORCE_STOP_CHARGING, START_SESSION_FAIL, STATE_GROUPS } from 'config';
import amplitude from 'service/amplitude';
import repo from 'service/repository';
import { chargingPowerTwoDecimal } from 'utils/helper';
import { useInterval } from 'utils/use-interval';
import { ChargingRecordsProvider, useChargingRecordsProvider } from './use-charging-records';
import useChargingAnalytics from './use-charging-analytics';
import styles from './index.module.sass';
import './transition.css';
// image
import imgConnect from 'images/icon-connect.png';
import { ReactComponent as IconPnc } from 'images/icon-pnc.svg';
import iconUnplug from 'images/icon-unplug.svg';

// dayjs extends
var relativeTime = require('dayjs/plugin/relativeTime');
dayjs.extend(relativeTime);

const cx = classnames.bind(styles);

export const replaceWithBr = text => {
  const textGroup = text.split('\n');
  return textGroup.map((x, key) => {
    if (key === textGroup.length - 1) {
      return <React.Fragment key={key}>{x}</React.Fragment>;
    }
    return (
      <React.Fragment key={key}>
        {x}
        <br />
      </React.Fragment>
    );
  });
};

const NavigateButton = ({ fetchGeoLocation, geolocationFetchFail, isCharging, onNavigateClick }) => {
  return (
    <div className={cx(styles['navigate-button'], styles.button)}>
      <div
        className={styles.text}
        onClick={() => {
          fetchGeoLocation();
          onNavigateClick?.();
        }}
      >
        {/* for styling */}
        {isCharging ? '導航前往取車' : <Button>導航前往取車</Button>}
      </div>
      {geolocationFetchFail && <div className={styles.error}>讀取位置失敗，請再次嘗試。</div>}
    </div>
  );
};

const ChargingInfoList = ({ data, accTime, chargingAmount, idleFee, showEndedInfo, isCharging }) => {
  const getUpdatedAtLabel = () => {
    if (STATE_GROUPS.STOPPED.includes(data.state)) return '停止時間';
    if (STATE_GROUPS.ENDED.includes(data.state) || STATE_GROUPS.START_SESSION_FAIL.includes(data.state))
      return '完成時間';
    return '更新時間';
  };

  const getUpdatedAtTime = () => {
    const time = () => {
      if (STATE_GROUPS.STOPPED.includes(data.state)) return data.stopAt;
      if (STATE_GROUPS.ENDED.includes(data.state)) return data.disabledChargingAt;
      return data.updatedAt;
    };
    if (STATE_GROUPS.START_SESSION_FAIL.includes(data.state)) return '-';
    return dayjs(time()).format('YYYY/M/D HH:mm:ss');
  };

  const getStartTime = () => {
    if (STATE_GROUPS.START_SESSION_FAIL.includes(data.state)) return '-';
    return dayjs(data.enabledChargingAt).format('YYYY/M/D HH:mm:ss');
  };

  const getAmountLabel = () => (showEndedInfo ? '應付款金額' : '預計充電費用');

  const rate = () => {
    if (parseFloat(data.evse.chargingPeriod.pricePerMinute) !== 0) {
      return `${data.evse.chargingPeriod.pricePerMinute}元/分鐘`;
    } else if (parseFloat(data.evse.chargingPeriod.pricePerKWh) !== 0) {
      return `${data.evse.chargingPeriod.pricePerKWh}元/度`;
    } else {
      return '';
    }
  };

  // 有佔位費
  const showIdleFeeInfo = data.evse.chargingPeriod.idleFeeEnabled;
  const showFeeInfo = showEndedInfo || showIdleFeeInfo;

  return (
    <div className={styles.detail}>
      <Mentos state={data.state} showCheckIcon={STATE_GROUPS.ENDED.includes(data.state)} />
      <div className={styles.basic}>
        <div className={styles.code}>{data.evse.uid}</div>
        <div className={styles.info}>
          <div>
            <div className={styles.name}>一般費率</div>
            <div className={styles.value}>{rate()}</div>
          </div>
          <div>
            <div className={styles.name}>最大充電功率</div>
            <div className={styles.value}>{chargingPowerTwoDecimal(data.evse.watt)} kw</div>
          </div>
        </div>
      </div>
      <ul className={styles.list}>
        <li className={styles.item}>
          <div className={styles.name}>累計充電時間</div>
          <div className={styles.value}>{accTime} 分</div>
        </li>
        <ul className={styles.more}>
          <li className={styles.item}>
            <div className={styles.name}>開始時間</div>
            <div className={styles.value}>{getStartTime()}</div>
          </li>
          <li className={styles.item}>
            <div className={styles.name}>{getUpdatedAtLabel()}</div>
            <div className={styles.value}>{getUpdatedAtTime()}</div>
          </li>
        </ul>
        <li className={styles.item}>
          <div className={styles.name}>充電度數</div>
          <div className={styles.value}>{chargingPowerTwoDecimal(data.energy)} kWh</div>
        </li>

        {showFeeInfo && (
          <div className={styles['idle-fee-container']}>
            {showIdleFeeInfo && (
              <li className={styles.item}>
                <div className={styles.name}>超時佔位</div>
                <div className={styles.value}>{formatSeconds(data.idleTimePerSecond)}</div>
              </li>
            )}
            {showEndedInfo && (
              <li className={`${styles.item} ${styles.amount}`}>
                <div className={styles.name}>充電費用</div>
                <div className={styles.value}>{chargingAmount - idleFee}元</div>
              </li>
            )}

            {showIdleFeeInfo && (
              <li className={`${styles.item} ${styles.amount}`}>
                <div className={styles.name}>
                  超時佔位費
                  <br />
                  <p className={styles['idle-fee-desc']}>
                    {(data.idleTimePerSecond / 60.0).toFixed(2)} 分鐘 (NT$ {data.evse.chargingPeriod.idleFeePerMinute}
                    /分鐘)
                  </p>
                </div>
                <div className={styles.value}>{idleFee}元</div>
              </li>
            )}
          </div>
        )}
        {/* 總金額 */}
        <li className={`${styles.item} ${styles.amount}`}>
          <div className={styles.name}>{getAmountLabel()}</div>
          <div className={styles.value}>{chargingAmount}元</div>
        </li>

        {/* 退還授權金額資訊 */}
        {showEndedInfo && (
          <li className={`${styles.item} ${styles.amount}`}>
            <div className={styles.name}>
              <div>退還授權金額</div>
            </div>
            <div className={styles['value-red']}>{data.authAmount - chargingAmount}元</div>
          </li>
        )}
      </ul>

      {showEndedInfo && (
        <div className={styles.notice}>
          <div className={styles.title}>備註</div>
          <div className={styles.content}>
            我們已通知銀行退還授權金額。款項預計 7~28 個工作天恢復至您的原卡片額度。 (依各銀行作業為準)
          </div>
        </div>
      )}

      {!showEndedInfo && (
        <div className={styles.hint}>
          <img src={iconUnplug} alt="unplug-hint" />
          {isCharging ? (
            <span>
              系統於<strong>達授權額度上限</strong>
              後自動終止。請注意，未達授權額度，拔槍前的微電流傳輸仍計入充電費用，建議滿電後儘速離場。
            </span>
          ) : (
            <span>請由車機停止並解鎖充電槍，掛回後系統將自動扣款。</span>
          )}
        </div>
      )}
    </div>
  );
};

const WaitingView = ({ serialNumber, ...chargingRecordState }) => {
  const TRYING_TO_CONNECT_THRESHOLD = 30;
  const { chargingData: data, fetchChargingRecord, refreshing, setRefreshing } = chargingRecordState;

  const [autoRefreshCount, setAutoRefreshCount] = useState(0);
  const [autoRefreshDelay, setAutoRefreshDelay] = useState(15000);
  const [isTryingToConnect, setIsTryingToConnect] = useState(data.state !== START_SESSION_FAIL);
  const autoRefreshMaxCount = 12;

  useEffect(() => {
    amplitude.chargingPlugHintPageView();
  }, []);

  useEffect(() => {
    if (!isTryingToConnect) return;

    let elapsedSeconds = 0;
    const timer = setInterval(() => {
      elapsedSeconds += 1;
      if (elapsedSeconds >= TRYING_TO_CONNECT_THRESHOLD) {
        clearInterval(timer);
        setIsTryingToConnect(false);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [isTryingToConnect]);

  useInterval(() => {
    fetchChargingRecord();
    setAutoRefreshCount(count => count + 1);
  }, autoRefreshDelay);

  useEffect(() => {
    if (autoRefreshCount === autoRefreshMaxCount) {
      setAutoRefreshDelay(null);
    }
  }, [autoRefreshCount]);

  const onRefresh = () => {
    amplitude.chargingPlugRetryButtonOnClick({
      serialNumber,
      evseId: data.evseId,
      evseName: data.evse.uid,
      email: data.email,
    });

    setRefreshing(true);
    fetchChargingRecord();
    setIsTryingToConnect(true);
  };

  return (
    <div className={styles['in-waiting']}>
      <div className={styles.heading}>
        {refreshing || isTryingToConnect ? (
          <>
            <div className={styles.title}>充電樁啟用中</div>
            <div className={styles.subtitle}>請保持插槍，等待系統連接</div>
          </>
        ) : (
          <>
            <div className={styles.title}>連線未成功</div>
            <div className={styles.subtitle}>請檢查充電槍有確實扣緊， 點擊下方按鈕，重新連線充電樁。</div>
          </>
        )}
        <div className={styles.image}>
          <img src={imgConnect} alt="connect" />
          <div className={styles['dots-group']}>
            <div />
            <div />
            <div />
          </div>
        </div>
      </div>
      <div className={styles.refresh}>
        {refreshing || isTryingToConnect ? (
          <span className={styles.span}>正在連結充電設備</span>
        ) : (
          <Button className={styles.button} onClick={onRefresh}>
            重新連線
          </Button>
        )}
      </div>
    </div>
  );
};

const ChargingDetailView = ({ serialNumber, ...chargingRecordState }) => {
  const [autoRefreshDelay] = useState(15000);
  const {
    chargingData: data,
    geolocationFetchFail,
    NavigationMapDialog,
    fetchGeoLocation,
    fetchChargingRecord,
  } = chargingRecordState;

  const accTimeValue = getAccTime(data);
  const chargingAmountValue = getChargingAmount(data);
  const idleFeeValue = getIdleFee(data);
  const [isStoppingCharging, setIsStoppingCharging] = useState(false);
  const [isStoppingChargingError, setIsStoppingChargingError] = useState(false);

  useInterval(() => {
    fetchChargingRecord();
  }, autoRefreshDelay);

  useChargingAnalytics({
    data,
    serialNumber,
    watt: chargingPowerTwoDecimal(data.energy),
    idleFee: idleFeeValue,
    accTime: accTimeValue,
    amount: chargingAmountValue,
  });

  const { title, subTitle } = getHeadingContent(data);
  const isServiceEnded = STATE_GROUPS.ENDED.includes(data.state);
  const isCharging = STATE_GROUPS.CHARGING.includes(data.state);
  const isTimeout = data.state === START_SESSION_FAIL;
  const showEndedInfo = isServiceEnded || (isTimeout && data.stopReason === 'charging_no_energy');

  const onStopCharging = async () => {
    setIsStoppingChargingError(false);
    setIsStoppingCharging(true);

    amplitude.chargingPageStopChargingOnClick({ state: data.state, stopReason: data.stopReason });
    const [error] = await repo.forceStopChargingRecord(data.id);

    if (error) {
      const errorMessage = error?.response?.data?.error ?? error.message;
      amplitude.chargingPageStopChargingError({ state: data.state, errorMessage });
      setIsStoppingChargingError(true);
    }

    fetchChargingRecord();
    setIsStoppingCharging(false);
  };

  return (
    <div className={styles['in-charging']}>
      {NavigationMapDialog()}
      <div className={styles.heading}>
        <div className={styles.title} data-testid="title">
          {replaceWithBr(title)}
        </div>
        {subTitle && (
          <div className={styles['sub-title']} data-testid="subtitle">
            {replaceWithBr(subTitle)}
          </div>
        )}
      </div>

      <ChargingInfoList
        data={data}
        accTime={accTimeValue}
        chargingAmount={chargingAmountValue}
        idleFee={idleFeeValue}
        isCharging={isCharging}
        showEndedInfo={showEndedInfo}
      />

      {isCharging && (
        <div className={styles['stop-charging-button']}>
          <Button onClick={onStopCharging} disabled={isStoppingCharging || isStoppingChargingError}>
            {isStoppingChargingError ? '停止失敗，請手動拔槍' : '停止充電'}
          </Button>
        </div>
      )}

      {!showEndedInfo && !isTimeout && (
        <NavigateButton
          isCharging={isCharging}
          fetchGeoLocation={fetchGeoLocation}
          geolocationFetchFail={geolocationFetchFail}
          onNavigateClick={() => {
            amplitude.chargingNavigationOnClick({
              serialNumber,
              evseId: data.evseId,
              evseName: data.evse.uid,
              email: data.email,
            });
          }}
        />
      )}
    </div>
  );
};

// ----------------------------------------------------------------------
// Main Component
// ----------------------------------------------------------------------

const ChargingRecords = () => {
  const { serialNumber } = useParams();
  const chargingRecordState = useChargingRecordsProvider();
  const { loaded, chargingData } = chargingRecordState;

  const modalConfig = useMemo(() => {
    if (!loaded || !chargingData) return { isOpen: false };

    return getNotificationModalContent(chargingData);
  }, [loaded, chargingData?.state, chargingData?.stopReason]); // eslint-disable-line

  useEffect(() => {
    if (loaded && modalConfig?.isOpen && modalConfig?.event) {
      modalConfig.event();
    }
  }, [loaded, modalConfig?.isOpen, modalConfig?.event]); // eslint-disable-line

  if (!loaded) {
    return <Loading loadingText="請稍候..." />;
  }

  // 狀態判斷
  const isWaiting = STATE_GROUPS.WAITING.includes(chargingData.state);
  const isCharging = STATE_GROUPS.CHARGING.includes(chargingData.state);

  const hasAllContactInfoFilled =
    chargingData?.email &&
    chargingData.email !== '<nil>' &&
    chargingData?.userPhone &&
    chargingData.userPhone !== '<nil>';

  return (
    <Wrapper>
      <ContactModal data={chargingData} isOpen={isCharging && !hasAllContactInfoFilled} />
      <NotificationModal
        isOpen={modalConfig.isOpen}
        title={modalConfig.title}
        description={modalConfig.description}
        buttonText={modalConfig.buttonText || '我知道了'}
        onButtonClick={modalConfig?.onButtonClick || (() => {})}
      />
      <IconPnc className={styles.png} />
      <CSSTransition appear={true} in={isWaiting} timeout={300} classNames="waiting" mountOnEnter unmountOnExit>
        <WaitingView {...chargingRecordState} serialNumber={serialNumber} />
      </CSSTransition>
      <CSSTransition in={!isWaiting} timeout={300} classNames="charging" mountOnEnter unmountOnExit>
        <ChargingDetailView {...chargingRecordState} serialNumber={serialNumber} />
      </CSSTransition>
    </Wrapper>
  );
};

const ChargingRecordsWrapper = ({ initial }) => {
  return (
    <ChargingRecordsProvider initial={initial}>
      <ChargingRecords />
    </ChargingRecordsProvider>
  );
};

// ----------------------------------------------------------------------
//  helper functions
// ----------------------------------------------------------------------

const getAccTime = data => {
  if (STATE_GROUPS.START_SESSION_FAIL.includes(data.state)) return 0;

  const endingStateTime = () => {
    if (data.state === FINISH_CHARGING) return data.disabledChargingAt;
    if ([COMPLETE_SESSION, FORCE_STOP_CHARGING].includes(data.state)) return data.stopAt;
    return data.updatedAt;
  };

  const startTime = dayjs(data.enabledChargingAt).startOf('second');
  const endTime = dayjs(endingStateTime()).startOf('second');

  if (!startTime.isValid() || !endTime.isValid()) return 0;
  return Math.ceil(dayjs(endTime).diff(startTime, 'second') / 60);
};

const getChargingAmount = data => {
  if (STATE_GROUPS.ENDED.includes(data.state)) {
    return data.amount;
  }

  if (parseFloat(data.evse.chargingPeriod.pricePerMinute) !== 0) {
    return Math.ceil(parseFloat(data.evse.chargingPeriod.pricePerMinute) * getAccTime(data));
  } else if (parseFloat(data.evse.chargingPeriod.pricePerKWh) !== 0) {
    return Math.ceil(parseFloat(data.evse.chargingPeriod.pricePerKWh) * parseFloat(data.energy));
  } else {
    return 0;
  }
};

const formatSeconds = seconds => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return [hours, minutes, secs].map(num => String(num).padStart(2, '0')).join(':');
};

const getIdleFee = data => {
  const fee = (data.idleTimePerSecond / 60.0) * parseFloat(data.evse.chargingPeriod.idleFeePerMinute);
  return isNaN(fee) ? 0 : Math.round(fee);
};

const getHeadingContent = chargingData => {
  const idleFeeSubtitle = chargingData.evse.chargingPeriod.idleFeeEnabled
    ? chargingData.idleTimePerSecond > 0
      ? '超時佔位費已開始計費\n請盡快移車以停止費用累計'
      : '結束充電 10 分鐘內未駛離\n將收取超時佔位費'
    : null;

  if (STATE_GROUPS.CHARGING.includes(chargingData.state)) {
    return {
      title: '您的車輛\n正在充電中',
      subTitle: idleFeeSubtitle,
    };
  }

  if (STATE_GROUPS.STOPPED.includes(chargingData.state)) {
    return {
      title: '已結束充電，請前往取車',
      subTitle: idleFeeSubtitle || '系統將於拔槍後按實際用量結算並退還差額。',
    };
  }

  if (chargingData.state === START_SESSION_FAIL && chargingData?.stopReason === 'start_session_timeout') {
    return {
      title: '已結束充電',
      subTitle: '系統將於拔槍後按實際用量結算並退還差額。',
    };
  }

  if (chargingData.state === START_SESSION_FAIL && chargingData?.stopReason === 'charging_no_energy') {
    return {
      title: '已完成充電付款\n期待你再次使用服務',
      subTitle: null,
    };
  }

  // ENDED or others
  return {
    title: '已完成充電付款\n期待你再次使用服務',
    subTitle: null,
  };
};

const getNotificationModalContent = chargingData => {
  const commonParams = {
    state: chargingData.state,
    stopReason: chargingData.stopReason,
  };
  const scenarios = [
    {
      // 授權金額達到閾值 (90%) 停止充電
      condition: data => data.state === FORCE_STOP_CHARGING && data.stopReason === 'auth_amount_threshold',
      content: {
        title: '計費達預扣額度，充電自動停止',
        description: '本次充電已達授權金額上限，系統已停止供電。最終結算將以實際發票金額為準。',
        isOpen: true,
      },
      event: () => amplitude.chargingPageOverChargePageView({ ...commonParams }),
      onButtonClick: () => amplitude.chargingPageOverChargeButtonOnClick({ ...commonParams }),
    },
    {
      // 系統異常停止充電 (管理員強制停止)
      condition: data => data.state === FORCE_STOP_CHARGING && data?.stopReason === 'admin_force_stop',
      content: {
        title: '系統異常，充電自動停止',
        description: '偵測到電力傳輸異常，為確保用電安全，系統已主動停止供電。最終結算將以實際發票金額為準。',
        isOpen: true,
      },
      event: () => amplitude.chargingPageSystemErrorPageView({ ...commonParams }),
      onButtonClick: () => amplitude.chargingPageSystemErrorButtonOnClick({ ...commonParams }),
    },
    {
      // 系統異常停止充電 (300 秒內電量沒有超過零)
      condition: data => data.state === START_SESSION_FAIL && data.stopReason === 'charging_no_energy',
      content: {
        title: '系統異常，充電自動停止',
        description: '系統偵測異常，為確保用電安全，已停止供電。系統將於拔槍後按實際用量結算並退還差額。',
        isOpen: true,
      },
      event: () => amplitude.chargingPageSystemErrorPageView({ ...commonParams }),
      onButtonClick: () => amplitude.chargingPageSystemErrorButtonOnClick({ ...commonParams }),
    },
    {
      // 連結槍時間逾時 (120 秒內沒有開始充電)
      condition: data => data.state === START_SESSION_FAIL && data?.stopReason === 'start_session_timeout',
      content: {
        title: '系統連線超時，自動結束充電',
        description:
          '請檢查車輛和充電樁的連接狀態，並重新掃碼。如問題持續，請改用鄰近車位的充電樁，或連繫客服尋求協助。',
        isOpen: true,
      },
      event: () => amplitude.chargingTimeoutPageView({ ...commonParams }),
      onButtonClick: () => amplitude.chargingTimeoutButtonOnClick({ ...commonParams }),
    },
  ];

  const match = scenarios.find(s => s.condition(chargingData));
  return match ? { ...match.content, event: match.event, onButtonClick: match.onButtonClick } : { isOpen: false };
};

export default ChargingRecordsWrapper;
